import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/dialog';
import { Button } from '@ui/button';
import { Textarea } from '@ui/textarea';
import { Label } from '@ui/label';
import { RadioGroup, RadioGroupItem } from '@ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select';
import { useToast } from '@ui/use-toast';
import { submitFeedback } from '@lib/api';
import { toProblem } from '@lib/api/base';
import { Loader2, SendIcon } from 'lucide-react';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FeedbackDialog = ({ open, onOpenChange }: FeedbackDialogProps) => {
  const [feedbackType, setFeedbackType] = useState('Feature Request');
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast({
        title: 'Feedback Required',
        description: 'Please enter your feedback before submitting.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitFeedback({
        feedbackType,
        feedback,
        rating,
      });
      toast({
        title: 'Feedback Submitted',
        description: result.message || 'Thank you for your feedback! We appreciate your input.',
      });
      // Reset the form and close the dialog
      setFeedback('');
      setFeedbackType('Feature Request');
      setRating(undefined);
      onOpenChange(false);
    } catch (error) {
      const problem = toProblem(error);
      toast({
        title: 'Submission Error',
        description:
          problem.detail || problem.title || 'Failed to submit feedback. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>Submit Feedback</DialogTitle>
          <DialogDescription>
            Share your thoughts, report issues, or suggest improvements. Your feedback helps us make
            the product better!
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="feedback-type">Feedback Type</Label>
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger id="feedback-type" data-testid="feedback-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Feature Request">Feature Request</SelectItem>
                <SelectItem value="Bug Report">Bug Report</SelectItem>
                <SelectItem value="UX/UI Improvement">UX/UI Improvement</SelectItem>
                <SelectItem value="General Feedback">General Feedback</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="feedback">Your Feedback</Label>
            <Textarea
              id="feedback"
              placeholder="Please describe your feedback in detail..."
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              className="min-h-[120px]"
              data-testid="feedback-textarea"
            />
          </div>

          <div className="grid gap-2">
            <Label>How would you rate your experience?</Label>
            <div className="flex flex-col items-center gap-2">
              <RadioGroup
                value={rating?.toString()}
                onValueChange={value => setRating(parseInt(value))}
                className="grid grid-cols-5 w-full justify-items-center"
                data-testid="feedback-rating-group"
              >
                {[1, 2, 3, 4, 5].map(value => (
                  <div key={value} className="flex flex-col items-center">
                    <RadioGroupItem
                      value={value.toString()}
                      id={`rating-${value}`}
                      data-testid={`rating-${value}`}
                    />
                    <Label htmlFor={`rating-${value}`} className="mt-1">
                      {value}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <div className="flex justify-between w-full text-xs text-muted-foreground px-1">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            data-testid="cancel-feedback-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            variant="default"
            data-testid="submit-feedback-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <SendIcon className="mr-2 h-4 w-4" />
                Submit Feedback
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
